/**
 * Custom Product Gallery - Clean Architecture
 * Manages Swiper gallery with integrated slideshow functionality
 */

// ============================================================================
// UTILITY: Wait for external library
// ============================================================================
class LibraryLoader {
  static async waitForLibrary(name, checkFn, maxRetries = 10, interval = 100) {
    for (let i = 0; i < maxRetries; i++) {
      if (checkFn()) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, interval));
    }
    return false;
  }
}

// ============================================================================
// EVENT EMITTER: Simple Observer Pattern Implementation
// ============================================================================
class EventEmitter {
  constructor() {
    this.events = {};
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);

    // Return unsubscribe function
    return () => {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    };
  }

  /**
   * Emit an event
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    if (!this.events[event]) return;

    this.events[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event handler for "${event}":`, error);
      }
    });
  }

  /**
   * Remove all listeners for an event or all events
   * @param {string} [event] - Event name (optional)
   */
  off(event) {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

// ============================================================================
// SLIDESHOW CONTROLLER: Manages button-based image slideshow
// ============================================================================
class SlideshowController {
  /**
   * @param {HTMLElement} container - Container element for slideshow images
   * @param {NodeList|Array} buttons - Slideshow control buttons
   */
  constructor(container, buttons) {
    this.container = container;
    this.buttons = Array.from(buttons);
    this.images = Array.from(container.querySelectorAll('.slideshow-image'));
    this.buttonsWithImages = this.buttons.filter(btn => btn.dataset.imageUrl);
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.state = {
      currentActiveButton: null,
      currentAutoAdvanceIndex: 1,
      userHasInteracted: false,
      isOnFirstSlide: true,
      autoAdvanceInterval: null
    };

    this.boundHandlers = new Map();
    this.unsubscribers = [];
    this.init();
  }

  init() {
    this.setupButtons();
    this.activateFirstButton();
  }

  /**
   * Subscribe to Swiper events via EventEmitter
   * @param {EventEmitter} eventEmitter - Event emitter instance
   * @param {object} currentState - Current Swiper state if already initialized
   */
  subscribeToSwiperEvents(eventEmitter, currentState = null) {
    const unsubInit = eventEmitter.on('swiper:init', ({ activeIndex }) => {
      this.state.isOnFirstSlide = (activeIndex === 0);
      if (this.state.isOnFirstSlide) {
        this.startAutoAdvance();
      }
    });

    const unsubSlideChange = eventEmitter.on('swiper:slideChange', ({ activeIndex }) => {
      this.onSlideChange(activeIndex);
    });

    this.unsubscribers.push(unsubInit, unsubSlideChange);

    if (currentState && currentState.activeIndex === 0) {
      this.state.isOnFirstSlide = true;
      this.startAutoAdvance();
    }
  }

  /**
   * Handle slide change
   * @param {number} activeIndex - Current active slide index
   */
  onSlideChange(activeIndex) {
    const wasOnFirstSlide = this.state.isOnFirstSlide;
    this.state.isOnFirstSlide = (activeIndex === 0);

    if (wasOnFirstSlide && !this.state.isOnFirstSlide) {
      this.stopAutoAdvance();
    }

    if (!wasOnFirstSlide && this.state.isOnFirstSlide && !this.state.userHasInteracted) {
      this.activateFirstButton();
      this.state.currentAutoAdvanceIndex = 1;
      this.startAutoAdvance();
    }
  }

  // -------------------------------------------------------------------------
  // Button Management
  // -------------------------------------------------------------------------
  setupButtons() {
    this.buttons.forEach(button => {
      const handler = (e) => {
        e.preventDefault();
        this.handleButtonClick(button);
      };
      this.boundHandlers.set(button, handler);
      button.addEventListener('click', handler);
    });
  }

  handleButtonClick(button) {
    this.state.userHasInteracted = true;
    this.stopAutoAdvance();
    this.state.currentAutoAdvanceIndex = 1;

    const buttonId = button.dataset.buttonId;
    const imageUrl = button.dataset.imageUrl;

    if (!imageUrl) return;

    if (this.state.currentActiveButton === buttonId) {
      this.restoreOriginalImage();
      return;
    }

    this.showButtonImage(buttonId);
  }

  activateFirstButton() {
    const firstButton = this.buttons.find(btn => btn.dataset.buttonId === '1');
    if (firstButton && firstButton.dataset.imageUrl) {
      this.showButtonImage('1');
    }
  }

  showButtonImage(buttonId) {
    const targetImage = this.container.querySelector(`[data-button="${buttonId}"]`);
    const targetButton = this.buttons.find(btn => btn.dataset.buttonId === buttonId);

    if (!targetImage) {
      return;
    }

    this.images.forEach(img => {
      img.classList.remove('slideshow-image--active');
      if (img !== targetImage) {
        img.style.display = 'none';
      }
    });

    targetImage.style.display = 'block';
    targetImage.classList.add('slideshow-image--active');

    this.buttons.forEach(btn => btn.classList.remove('active'));
    if (targetButton) {
      targetButton.classList.add('active');
    }

    this.state.currentActiveButton = buttonId;
  }

  restoreOriginalImage() {
    const originalImage = this.container.querySelector('[data-original="true"]');

    this.images.forEach(img => {
      img.classList.remove('slideshow-image--active');
      if (!img.dataset.original) {
        img.style.display = 'none';
      }
    });

    if (originalImage) {
      originalImage.classList.add('slideshow-image--active');
    }

    this.buttons.forEach(btn => btn.classList.remove('active'));
    this.state.currentActiveButton = null;
  }

  // -------------------------------------------------------------------------
  // Auto-Advance
  // -------------------------------------------------------------------------
  startAutoAdvance() {
    if (this.state.autoAdvanceInterval) return;
    if (this.buttonsWithImages.length <= 1 || this.prefersReducedMotion) return;

    this.state.autoAdvanceInterval = setInterval(() => {
      try {
        if (this.state.userHasInteracted || !this.state.isOnFirstSlide) {
          this.stopAutoAdvance();
          return;
        }

        if (!this.container || !this.container.isConnected) {
          this.stopAutoAdvance();
          return;
        }

        const button = this.buttonsWithImages[this.state.currentAutoAdvanceIndex];
        if (button && button.dataset.buttonId) {
          this.showButtonImage(button.dataset.buttonId);
        }

        this.state.currentAutoAdvanceIndex = (this.state.currentAutoAdvanceIndex + 1) % this.buttonsWithImages.length;
      } catch (error) {
        console.error('Error in auto-advance:', error);
        this.stopAutoAdvance();
      }
    }, 2000);
  }

  stopAutoAdvance() {
    if (this.state.autoAdvanceInterval) {
      clearInterval(this.state.autoAdvanceInterval);
      this.state.autoAdvanceInterval = null;
    }
  }

  // -------------------------------------------------------------------------
  // Cleanup
  // -------------------------------------------------------------------------
  destroy() {
    this.stopAutoAdvance();

    this.unsubscribers.forEach(unsubscribe => unsubscribe());
    this.unsubscribers = [];

    this.buttons.forEach(button => {
      const handler = this.boundHandlers.get(button);
      if (handler) {
        button.removeEventListener('click', handler);
      }
    });
    this.boundHandlers.clear();
  }
}

// ============================================================================
// SWIPER GALLERY: Manages Swiper instance and interactions
// ============================================================================
class SwiperGallery extends EventEmitter {
  /**
   * @param {HTMLElement} element - Swiper container element
   * @param {HTMLElement} thumbsElement - Thumbs swiper container element (optional)
   */
  constructor(element, thumbsElement = null) {
    super();
    this.element = element;
    this.thumbsElement = thumbsElement;
    this.progressFill = document.querySelector('.progress-fill');
    this.swiper = null;
    this.thumbsSwiper = null;
  }

  init() {
    if (!this.element) {
      return null;
    }

    // Initialize thumbs swiper first (if exists and is desktop)
    if (this.thumbsElement && window.innerWidth >= 750) {
      this.thumbsSwiper = new Swiper(this.thumbsElement, {
        spaceBetween: 12,
        slidesPerView: 'auto',
        freeMode: false,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
      });
    }

    this.swiper = new Swiper(this.element, {
      slidesPerView: 1,
      spaceBetween: 10,
      speed: 300,
      touchRatio: 1,
      touchAngle: 45,
      threshold: 30,
      autoplay: false,
      loop: false,
      centeredSlides: false,
      resistance: true,
      resistanceRatio: 0.85,
      preventClicksPropagation: true,
      preventClicks: true,
      thumbs: this.thumbsSwiper ? {
        swiper: this.thumbsSwiper,
        autoScrollOffset: 1
      } : undefined,

      on: {
        init: (swiper) => {
          const activeIndex = swiper.realIndex ?? swiper.activeIndex;
          this.updateProgressBar(swiper);
          if (!('ontouchstart' in window)) {
            this.setupDesktopNavigation(swiper);
          }
          this.emit('swiper:init', { activeIndex });
        },
        slideChange: (swiper) => {
          const activeIndex = swiper.realIndex ?? swiper.activeIndex;
          this.updateProgressBar(swiper);
          this.emit('swiper:slideChange', { activeIndex });
        },
        transitionStart: (swiper) => {
          swiper.el.classList.add('swiper-animating');
        },
        transitionEnd: (swiper) => {
          swiper.el.classList.remove('swiper-animating');
        }
      }
    });

    return this.swiper;
  }

  // -------------------------------------------------------------------------
  // Progress Bar
  // -------------------------------------------------------------------------
  updateProgressBar(swiper) {
    if (!this.progressFill) return;

    const activeIndex = swiper.realIndex ?? swiper.activeIndex;
    const totalSlides = swiper.slides.length;

    if (totalSlides === 0) return;

    const segmentWidth = 100 / totalSlides;
    const position = activeIndex * segmentWidth;

    this.progressFill.style.width = `${segmentWidth}%`;
    this.progressFill.style.transform = `translateX(${(position / segmentWidth) * 100}%)`;
  }

  // -------------------------------------------------------------------------
  // Desktop Click Navigation
  // -------------------------------------------------------------------------
  setupDesktopNavigation(swiper) {
    const handleMouseMove = (e) => {
      if (swiper.el.classList.contains('swiper-animating')) return;

      const rect = swiper.el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const isLeftHalf = mouseX < rect.width / 2;
      const currentIndex = swiper.realIndex ?? swiper.activeIndex;

      if (isLeftHalf && currentIndex > 0) {
        swiper.el.style.cursor = 'url(https://storage.googleapis.com/lemonetti-images/Icons/icon-swipe_left-64.svg) 32 32, pointer';
      } else if (!isLeftHalf && currentIndex < swiper.slides.length - 1) {
        swiper.el.style.cursor = 'url(https://storage.googleapis.com/lemonetti-images/Icons/icon-swipe_right-64.svg) 32 32, pointer';
      } else {
        swiper.el.style.cursor = 'default';
      }
    };

    const handleClick = (e) => {
      if (swiper.el.classList.contains('swiper-animating')) return;

      const rect = swiper.el.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const isLeftHalf = clickX < rect.width / 2;
      const currentIndex = swiper.realIndex ?? swiper.activeIndex;

      if (isLeftHalf && currentIndex > 0) {
        swiper.slidePrev();
      } else if (!isLeftHalf && currentIndex < swiper.slides.length - 1) {
        swiper.slideNext();
      }
    };

    const handleMouseLeave = () => {
      swiper.el.style.cursor = 'default';
    };

    swiper.el.addEventListener('mousemove', handleMouseMove);
    swiper.el.addEventListener('click', handleClick);
    swiper.el.addEventListener('mouseleave', handleMouseLeave);

    swiper.el.style.userSelect = 'none';
    swiper.el.style.webkitUserSelect = 'none';

    this.cleanupDesktopNav = () => {
      swiper.el.removeEventListener('mousemove', handleMouseMove);
      swiper.el.removeEventListener('click', handleClick);
      swiper.el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }

  // -------------------------------------------------------------------------
  // Cleanup
  // -------------------------------------------------------------------------
  destroy() {
    this.off();
    if (this.cleanupDesktopNav) {
      this.cleanupDesktopNav();
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
    }
  }
}

// ============================================================================
// MAIN CONTROLLER: Orchestrates everything
// ============================================================================
class CustomProductGallery {
  constructor() {
    this.swiperGallery = null;
    this.slideshowController = null;
  }

  async init() {
    const swiperLoaded = await LibraryLoader.waitForLibrary(
      'Swiper',
      () => typeof Swiper !== 'undefined'
    );

    if (!swiperLoaded) return;

    const galleryElement = document.querySelector('.custom-product__gallery');
    if (!galleryElement) return;

    const thumbsElement = document.querySelector('.custom-product__thumbs');

    this.swiperGallery = new SwiperGallery(galleryElement, thumbsElement);
    this.swiperGallery.init();

    const slideshowContainer = document.getElementById('slideshow-image-container');
    const slideshowButtons = document.querySelectorAll('[data-button-id]');

    if (slideshowContainer && slideshowButtons.length > 0) {
      this.slideshowController = new SlideshowController(
        slideshowContainer,
        slideshowButtons
      );

      const currentState = this.swiperGallery.swiper ? {
        activeIndex: this.swiperGallery.swiper.activeIndex,
        realIndex: this.swiperGallery.swiper.realIndex
      } : null;

      this.slideshowController.subscribeToSwiperEvents(this.swiperGallery, currentState);
    }
  }

  destroy() {
    if (this.slideshowController) {
      this.slideshowController.destroy();
    }
    if (this.swiperGallery) {
      this.swiperGallery.destroy();
    }
  }
}

// ============================================================================
// BOOTSTRAP: Initialize when DOM is ready
// ============================================================================
let galleryInstance = null;

function bootstrap() {
  galleryInstance = new CustomProductGallery();
  galleryInstance.init();
  window.customProductGallery = galleryInstance;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
